import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiChevronDown, FiChevronUp, FiShield, FiUsers, FiClock, FiStar, FiAward, FiCheckCircle } from 'react-icons/fi';
import {
    HiOutlineOfficeBuilding,
    HiOutlineHome,
    HiOutlineCog,
    HiOutlineSparkles,
    HiOutlineClipboardCheck,
    HiOutlineShieldCheck,
    HiOutlineAcademicCap,
    HiOutlineShoppingBag,
    HiOutlineHeart,
    HiOutlineLibrary,
    HiOutlineTruck
} from 'react-icons/hi';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';

const Services = () => {
    const { t } = useTranslation();

    const servicesList = [
        {
            id: 'commercial',
            title: t('servicesPage.items.commercial.title'),
            desc: t('servicesPage.items.commercial.desc'),
            image: '/images/site/commercial-cleaning.png',
            scopes: [
                t('servicesPage.items.commercial.f1'),
                t('servicesPage.items.commercial.f2'),
                t('servicesPage.items.commercial.f3'),
                t('servicesPage.items.commercial.f4'),
                t('servicesPage.items.commercial.f5')
            ],
            
            benefits: t('servicesPage.items.commercial.benefits')
        },
        {
            id: 'residential',
            title: t('servicesPage.items.residential.title'),
            desc: t('servicesPage.items.residential.desc'),
            image: '/images/site/residential-cleaning.png',
            scopes: [
                t('servicesPage.items.residential.f1'),
                t('servicesPage.items.residential.f2'),
                t('servicesPage.items.residential.f3'),
                t('servicesPage.items.residential.f4'),
                t('servicesPage.items.residential.f5')
            ],
            benefits: t('servicesPage.items.residential.benefits')
        },
        {
            id: 'industrial',
            title: t('servicesPage.items.industrial.title'),
            desc: t('servicesPage.items.industrial.desc'),
            image: '/images/site/industrial-cleaning-new.jpg',
            scopes: [
                t('servicesPage.items.industrial.f1'),
                t('servicesPage.items.industrial.f2'),
                t('servicesPage.items.industrial.f3'),
                t('servicesPage.items.industrial.f4'),
                t('servicesPage.items.industrial.f5')
            ],
            benefits: t('servicesPage.items.industrial.benefits')
        },
        {
            id: 'ropeAccess',
            title: t('servicesPage.items.ropeAccess.title'),
            desc: t('servicesPage.items.ropeAccess.desc'),
            image: '/images/site/rope-access.png',
            scopes: [
                t('servicesPage.items.ropeAccess.f1'),
                t('servicesPage.items.ropeAccess.f2'),
                t('servicesPage.items.ropeAccess.f3'),
                t('servicesPage.items.ropeAccess.f4')
            ],
            benefits: t('servicesPage.items.ropeAccess.benefits')
        },
        {
            id: 'floorCare',
            title: t('servicesPage.items.floorCare.title'),
            desc: t('servicesPage.items.floorCare.desc'),
            image: '/images/site/floor-care.png',
            scopes: [
                t('servicesPage.items.floorCare.f1'),
                t('servicesPage.items.floorCare.f2'),
                t('servicesPage.items.floorCare.f3'),
                t('servicesPage.items.floorCare.f4')
            ],
            benefits: t('servicesPage.items.floorCare.benefits')
        },
        {
            id: 'carpet',
            title: t('servicesPage.items.carpet.title'),
            desc: t('servicesPage.items.carpet.desc'),
            image: '/images/site/carpet-care.png',
            scopes: [
                t('servicesPage.items.carpet.f1'),
                t('servicesPage.items.carpet.f2'),
                t('servicesPage.items.carpet.f3'),
                t('servicesPage.items.carpet.f4')
            ],
            benefits: t('servicesPage.items.carpet.benefits')
        },
        {
            id: 'pest',
            title: t('servicesPage.items.pest.title'),
            desc: t('servicesPage.items.pest.desc'),
            image: '/images/site/pest-control.png',
            scopes: [
                t('servicesPage.items.pest.f1'),
                t('servicesPage.items.pest.f2'),
                t('servicesPage.items.pest.f3'),
                t('servicesPage.items.pest.f4')
            ],
            benefits: t('servicesPage.items.pest.benefits')
        },
        {
            id: 'facility',
            title: t('servicesPage.items.facility.title'),
            desc: t('servicesPage.items.facility.desc'),
            image: '/images/site/hero-facility-support-new.png',
            scopes: [
                t('servicesPage.items.facility.f1'),
                t('servicesPage.items.facility.f2'),
                t('servicesPage.items.facility.f3'),
                t('servicesPage.items.facility.f4')
            ],
            benefits: t('servicesPage.items.facility.benefits')
        }
    ];

    const whyChooseList = [
        t('servicesPage.whyChoose.items.workforce'),
        t('servicesPage.whyChoose.items.quality'),
        t('servicesPage.whyChoose.items.scheduling'),
        t('servicesPage.whyChoose.items.support'),
        t('servicesPage.whyChoose.items.safety'),
        t('servicesPage.whyChoose.items.custom'),
        t('servicesPage.whyChoose.items.pricing'),
        t('servicesPage.whyChoose.items.inspections')
    ];

    const industriesServedList = [
        { icon: HiOutlineOfficeBuilding, title: t('servicesPage.industriesServed.items.offices') },
        { icon: HiOutlineShoppingBag, title: t('servicesPage.industriesServed.items.malls') },
        { icon: HiOutlineHome, title: t('servicesPage.industriesServed.items.residential') },
        { icon: HiOutlineHeart, title: t('servicesPage.industriesServed.items.healthcare') },
        { icon: HiOutlineAcademicCap, title: t('servicesPage.industriesServed.items.education') },
        { icon: HiOutlineSparkles, title: t('servicesPage.industriesServed.items.hotels') },
        { icon: HiOutlineTruck, title: t('servicesPage.industriesServed.items.industrial') },
        { icon: HiOutlineLibrary, title: t('servicesPage.industriesServed.items.government') },
        { icon: HiOutlineOfficeBuilding, title: t('servicesPage.industriesServed.items.airports') },
        { icon: HiOutlineCog, title: t('servicesPage.industriesServed.items.construction') }
    ];

    const faqs = [
        { q: t('servicesPage.faq.items.q1.question'), a: t('servicesPage.faq.items.q1.answer') },
        { q: t('servicesPage.faq.items.q2.question'), a: t('servicesPage.faq.items.q2.answer') },
        { q: t('servicesPage.faq.items.q3.question'), a: t('servicesPage.faq.items.q3.answer') },
        { q: t('servicesPage.faq.items.q4.question'), a: t('servicesPage.faq.items.q4.answer') }
    ];

    const [openFaq, setOpenFaq] = useState(null);
    const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

    return (
        <div className="w-full">
            <SEO
                title={t('seo.services.title')}
                description={t('seo.services.description')}
                keywords="commercial cleaning bahrain, rope access window cleaning, marble polishing, pest control bahrain, facility manpower support"
            />

            {/* Hero Banner */}
            <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/site/floor-care.png"
                        alt="PrimeForce Services Scope"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-block px-4 py-1.5 bg-accent-500/20 text-accent-300 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-accent-500/30 backdrop-blur-sm"
                    >
                        {t('servicesPage.hero.badge')}
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                    >
                        {t('servicesPage.hero.titleStart')} <span className="text-accent-400">{t('servicesPage.hero.titleAccent')}</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm sm:text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
                    >
                        {t('servicesPage.hero.subtext')}
                    </motion.p>
                </div>
            </section>

            {/* Services Detailed List (8 Services) */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('servicesPage.main.subtitle')}
                        title={t('servicesPage.main.title')}
                        description={t('servicesPage.main.description')}
                    />

                    <div className="space-y-20">
                        {servicesList.map((service, index) => (
                            <motion.div
                                key={service.id}
                                id={service.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6 }}
                                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                            >
                                {/* Image */}
                                <div className={`relative group ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                        <img
                                            src={service.image}
                                            alt={service.title}
                                            className="w-full h-[220px] sm:h-[320px] md:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                                        <div className="absolute bottom-4 ltr:right-4 rtl:left-4 w-12 h-12 md:w-16 md:h-16 bg-accent-500 rounded-xl flex items-center justify-center shadow-lg">
                                            <span className="text-xl md:text-2xl font-bold text-white">0{index + 1}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 mb-6 leading-relaxed text-base md:text-lg">
                                        {service.desc}
                                    </p>

                                    {/* Scope Checklist */}
                                    <div className="mb-6">
                                        <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wider">{t('servicesPage.serviceScope')}</h4>
                                        <ul className="grid sm:grid-cols-2 gap-2.5">
                                            {service.scopes.map((scope, idx) => (
                                                <li key={idx} className="flex items-start gap-2.5 text-gray-700 text-sm">
                                                    <div className="w-5 h-5 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        <FiCheck className="text-success-600" size={12} />
                                                    </div>
                                                    <span className="min-w-0 flex-1 leading-snug">{scope}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Benefits Badge */}
                                    <div className="mb-8 bg-gray-50 border border-gray-100 rounded-xl p-4">
                                        <span className="font-semibold text-primary-700 text-sm block mb-1">{t('servicesPage.keyBenefits')}</span>
                                        <p className="text-gray-600 text-sm">{service.benefits}</p>
                                    </div>

                                    <Link
                                        to="/contact"
                                        className="btn-primary inline-flex items-center justify-center gap-2 max-w-full text-center whitespace-normal"
                                    >
                                        <span>{t('home.hero.ctaPrimary')}</span>
                                        <FiArrowRight className="rtl:rotate-180 flex-shrink-0" />
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose PrimeForce Grid */}
            <section className="section-padding bg-gray-50 border-y border-gray-100">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('servicesPage.whyChoose.subtitle')}
                        title={t('servicesPage.whyChoose.title')}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {whyChooseList.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: idx * 0.05 }}
                                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow"
                            >
                                <div className="w-10 h-10 bg-accent-100 text-accent-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <FiCheckCircle size={20} />
                                </div>
                                <span className="font-semibold text-gray-800 text-sm md:text-base">{item}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industries We Serve */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('servicesPage.industriesServed.subtitle')}
                        title={t('servicesPage.industriesServed.title')}
                    />

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
                        {industriesServedList.map((ind, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: idx * 0.04 }}
                                className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100 hover:border-primary-300 hover:bg-primary-50 transition-all group"
                            >
                                <div className="w-12 h-12 mx-auto mb-3 bg-white text-primary-600 rounded-xl flex items-center justify-center shadow-sm group-hover:bg-primary-500 group-hover:text-white transition-colors">
                                    <ind.icon size={24} />
                                </div>
                                <h4 className="font-semibold text-gray-800 text-sm">{ind.title}</h4>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section (NEW SECTION) */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom max-w-4xl">
                    <SectionHeading
                        subtitle={t('servicesPage.faq.subtitle')}
                        title={t('servicesPage.faq.title')}
                    />

                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: idx * 0.08 }}
                                className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm"
                            >
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    className="w-full p-6 text-left flex justify-between items-center gap-4 hover:bg-gray-50 transition-colors font-bold text-gray-800 text-base md:text-lg"
                                >
                                    <span>{faq.q}</span>
                                    {openFaq === idx ? <FiChevronUp size={24} className="text-primary-600" /> : <FiChevronDown size={24} className="text-gray-400" />}
                                </button>
                                <AnimatePresence>
                                    {openFaq === idx && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="border-t border-gray-100 bg-gray-50/50"
                                        >
                                            <p className="p-6 text-gray-600 leading-relaxed text-base">
                                                {faq.a}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 bg-primary-900 text-white">
                <div className="container-custom relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                            {t('servicesPage.cta.titleStart')} <span className="text-accent-400">{t('servicesPage.cta.titleAccent')}</span> {t('servicesPage.cta.titleEnd')}
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
                            {t('servicesPage.cta.description')}
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link to="/contact" className="btn-accent text-lg px-8 py-4">
                                {t('servicesPage.cta.button')}
                                <FiArrowRight className="ml-2 inline rtl:rotate-180" />
                            </Link>
                            <Link to="/contact" className="btn-secondary border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-4">
                                {t('servicesPage.cta.buttonSecondary')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Services;
